/**
 * CAT-1 / CAT-4 / CAT-5 — canonical metadata resolution for an applied
 * canned-job labor line.
 *
 * Historically `apply_canned_job` hardcoded every applied labor line to
 * `category='Canned'`, `bay_requirement='Flat'`, `skill_level=1`, so all
 * canned work mis-routed through scheduling / proficiency / training
 * (an unknown 'Canned' category falls back to Maintenance; bay 'Flat'
 * clears every bay gate; skill 1 lets any tech qualify).
 *
 * This pure resolver is the single source of truth for what an applied
 * canned labor line BECOMES. It is used two ways:
 *   1. The canned-job lines editor renders a live "routes to …" preview
 *      from it (so authors see real routing, not 'Canned').
 *   2. The `apply_canned_job` SQL RPC mirrors these exact rules; the
 *      migration source-pin test guards that the SQL keeps matching.
 *
 * When a line is bound to a catalog job (`catalogJobCode`) the resolved
 * row carries the canonical `category_key`, `sub_menu`, `skill_level`,
 * `minutes_default`, and `primary_bay_type`. When a line is custom
 * (no catalog binding) it gets an EXPLICIT, VISIBLE fallback that never
 * pretends to be a validated catalog job.
 */

import { CATEGORY_KEY_TO_PROFICIENCY } from '@/constants/proficiencyCategories';

/** The canonical jobs_catalog category keys (used for the custom guard). */
export const CANONICAL_CATEGORY_KEYS: ReadonlySet<string> = new Set(
  Object.keys(CATEGORY_KEY_TO_PROFICIENCY),
);

/** Catalog row fields the resolver reads (subset of jobs_catalog). */
export interface CatalogMetaRow {
  code: string;
  category_key: string;
  sub_menu: string | null;
  skill_level: string | null;
  minutes_default: number | null;
  primary_bay_type: string | null;
}

export interface CannedLineInput {
  /** Author-entered labor hours (the editor seeds this from the catalog). */
  laborHours: number | null;
  /** Optional canonical catalog code the line is bound to. */
  catalogJobCode: string | null;
}

export interface AppliedJobMetadata {
  category: string;
  submenu: string;
  standardMinutes: number;
  bayRequirement: string;
  skillLevel: number;
  catalogJobCode: string | null;
  /** 'catalog' when resolved from jobs_catalog; 'custom' for the fallback. */
  source: 'catalog' | 'custom';
}

const CUSTOM_FALLBACK_SUBMENU = 'Custom (canned)';
const SAFE_DEFAULT_MINUTES = 60;
/** Proficient — a safe middle, never the misleading novice-1. */
const CUSTOM_FALLBACK_SKILL = 4;

/**
 * Catalog skill text → numeric skill_level. Mirrors the client
 * AddJobDialog skillLevelMap (basic=2, intermediate=4, advanced=7,
 * expert=9) including the diagnostics advanced→9 boost. Unknown text
 * falls back to 4 (Proficient).
 */
export function cannedCatalogSkillLevel(
  skillText: string | null | undefined,
  categoryKey: string | null | undefined,
): number {
  const s = (skillText ?? '').trim().toLowerCase();
  const cat = (categoryKey ?? '').trim().toLowerCase();
  if (cat === 'diagnostics' && s === 'advanced') return 9;
  switch (s) {
    case 'basic':
      return 2;
    case 'intermediate':
      return 4;
    case 'advanced':
      return 7;
    case 'expert':
      return 9;
    default:
      return 4;
  }
}

function resolveMinutes(
  laborHours: number | null | undefined,
  catalogMinutes: number | null | undefined,
  isCatalog: boolean,
): number {
  // An explicit, positive author duration always wins (custom override).
  if (typeof laborHours === 'number' && Number.isFinite(laborHours) && laborHours > 0) {
    return Math.round(laborHours * 60);
  }
  // CAT-5: catalog-backed lines seed from the catalog default duration.
  if (
    isCatalog &&
    typeof catalogMinutes === 'number' &&
    Number.isFinite(catalogMinutes) &&
    catalogMinutes > 0
  ) {
    return Math.round(catalogMinutes);
  }
  // Zero / invalid / missing → safe, visible default rather than 0.0 hr.
  return SAFE_DEFAULT_MINUTES;
}

/**
 * Resolve the canonical jobs-row metadata an applied canned labor line
 * should carry.
 *
 * @param line          the canned labor line (hours + optional catalog code)
 * @param catalogRow    the jobs_catalog row matching `line.catalogJobCode`,
 *                      or null when the line is custom / the code resolves
 *                      to nothing
 * @param cannedCategory the free-text `canned_jobs.category` (used only as
 *                      a guarded fallback for custom lines)
 */
export function resolveCannedLineMetadata(
  line: CannedLineInput,
  catalogRow: CatalogMetaRow | null,
  cannedCategory: string | null,
  canonicalKeys: ReadonlySet<string> = CANONICAL_CATEGORY_KEYS,
): AppliedJobMetadata {
  if (catalogRow) {
    return {
      category: catalogRow.category_key,
      submenu: (catalogRow.sub_menu ?? '').trim() || 'General',
      standardMinutes: resolveMinutes(line.laborHours, catalogRow.minutes_default, true),
      bayRequirement: (catalogRow.primary_bay_type ?? '').trim() || 'Flat',
      skillLevel: cannedCatalogSkillLevel(catalogRow.skill_level, catalogRow.category_key),
      catalogJobCode: catalogRow.code,
      source: 'catalog',
    };
  }

  // Custom / non-catalog line. Honor the author's category ONLY if it is
  // a real canonical category_key; otherwise fall back to 'general'
  // (Maintenance) rather than feeding free text into canonical dispatch
  // as if it were validated. Never the misleading literal 'Canned'.
  const trimmedCanned = (cannedCategory ?? '').trim();
  const category = canonicalKeys.has(trimmedCanned) ? trimmedCanned : 'general';
  return {
    category,
    submenu: CUSTOM_FALLBACK_SUBMENU,
    standardMinutes: resolveMinutes(line.laborHours, null, false),
    bayRequirement: 'Flat',
    skillLevel: CUSTOM_FALLBACK_SKILL,
    catalogJobCode: null,
    source: 'custom',
  };
}
